import { graphql, useStaticQuery } from "gatsby"
import { CityNode, ProvinceNode, CountryNode } from "../utils/locationTypes"

interface PartnerTypeNode {
  uuid: string
  name: string
  lang: string
}
interface PartnerExtraNode {
  uuid: string
  name: string
  lang: string
}

interface QueryResult {
  allPartnerTypes: {
    nodes: PartnerTypeNode[]
  }
  allPartnerExtras: {
    nodes: PartnerExtraNode[]
  }
  allCities: {
    nodes: CityNode[]
  }
  allProvinces: {
    nodes: ProvinceNode[]
  }
  allCountries: {
    nodes: CountryNode[]
  }
}

const usePartnerAttributes = (lang: string) => {
  const data = useStaticQuery<QueryResult>(graphql`
    query AllPartnerFilters {
      allPartnerTypes: allStoryblokEntry(
        filter: { field_component: { eq: "partnerType" } }
      ) {
        nodes {
          uuid
          name: field_name_string
          lang
        }
      }
      allPartnerExtras: allStoryblokEntry(
        filter: { field_component: { eq: "partnerExtra" } }
      ) {
        nodes {
          uuid
          name: field_name_string
          lang
        }
      }
      allCities: allStoryblokEntry(
        filter: { field_component: { eq: "city" } }
      ) {
        nodes {
          uuid
          name: field_name_string
          province: field_province_string
          lang
        }
      }
      allProvinces: allStoryblokEntry(
        filter: { field_component: { eq: "province" } }
      ) {
        nodes {
          uuid
          name: field_name_string
          country: field_country_string
          lang
        }
      }
      allCountries: allStoryblokEntry(
        filter: { field_component: { eq: "country" } }
      ) {
        nodes {
          uuid
          name: field_name_string
          lang
        }
      }
    }
  `)

    // Filter options based on the current language
  const filteredCities = data.allCities.nodes.filter(city => city.lang === lang)
  const filteredProvinces = data.allProvinces.nodes.filter(province => province.lang === lang)
  const filteredCountries = data.allCountries.nodes.filter(country => country.lang === lang)
  const filteredExtras = data.allPartnerExtras.nodes.filter(extra => extra.lang === lang)
  const filteredTypes = data.allPartnerTypes.nodes.filter(type => type.lang === lang)

  // Build lookup maps
  const cityMap = new Map<string, CityNode>()
  filteredCities.forEach(city => {
    cityMap.set(city.uuid, city)
  })

  const provinceMap = new Map<string, ProvinceNode>()
  filteredProvinces.forEach(province => {
    provinceMap.set(province.uuid, province)
  })

  const countryMap = new Map<string, CountryNode>()
  filteredCountries.forEach(country => {
    countryMap.set(country.uuid, country)
  })

  return {
    allExtras: filteredExtras,
    allTypes: filteredTypes,
    cityMap,
    countryMap,
    provinceMap,
  }
}

export default usePartnerAttributes
